/**
 * AC-1478: `PriorityHistogram` component.
 *
 * Renders a Recharts BarChart of priority distribution for a single queue,
 * with a status chip filter (PENDING | PROCESSING | FAILED | COMPLETED | ALL).
 * Defaults to PENDING.
 *
 * ## States
 *
 * - **Loading**: shows a `data-testid="histogram-loading"` indicator while
 *   the query is fetching. The chart itself is not rendered.
 * - **Error**: shows an inline error message and a Retry button that calls
 *   the React Query `refetch()` function for the current cache slot.
 * - **Empty**: when the response has `total === 0`, shows a "No {status}
 *   items in this queue" message in place of the chart.
 * - **Hidden**: when the queue is non-priority (`priority_field === null`),
 *   the component returns `null`. A flat p=0 distribution carries no signal
 *   and was previously causing layout shift when a zero-item status tab
 *   collapsed the chart (AC-1589).
 * - **Populated**: bar chart + summary line `min={min} · max={max} · n={total}`.
 *
 * ## Wiring (handled by AC-1480, NOT this ticket)
 *
 * AC-1480 imports this component into `QueueDetailsPage.tsx`. Until then the
 * component is exported but not mounted. Tests verify the component in
 * isolation; the orphan check intentionally fails this ticket and clears in
 * AC-1480.
 *
 * ## Test coverage
 *
 * `frontend/src/components/Queue/__tests__/PriorityHistogram.test.tsx` covers
 * loading, error, empty, hidden, distinct-mode, range-mode, summary line,
 * chip default + switching, all-five-chip presence, dark-mode classes, and
 * accessibility.
 */

import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

import { useQueuePriorityHistogram } from '../../hooks/queries/useQueuePriorityHistogram';
import { HistogramStatus, Queue } from '../../types';

/**
 * Props for `PriorityHistogram`.
 *
 * The component takes the `queue` (or at minimum the `queue_id` and
 * `priority_field` slice) so it can decide whether to apply the
 * "FIFO + no data" hidden-state guard. The chip-row selector and chart are
 * driven by the hook response keyed on `queue.queue_id`.
 */
export interface PriorityHistogramProps {
  /** Queue this histogram describes. Only `queue_id` and `priority_field` are read. */
  queue: Pick<Queue, 'queue_id' | 'priority_field'>;
  /** Initial status chip selection. Defaults to PENDING. */
  initialStatus?: HistogramStatus;
  /** Number of bins requested from the server. Default 20. */
  bins?: number;
  /** Optional className to merge with the root wrapper. */
  className?: string;
}

const STATUS_OPTIONS: HistogramStatus[] = [
  'PENDING',
  'PROCESSING',
  'FAILED',
  'COMPLETED',
  'ALL',
];

/**
 * Tailwind classes for the active and inactive chip states. Both include
 * `dark:` variants so the chip row reads correctly under either theme.
 */
const ACTIVE_CHIP =
  'px-3 py-1 text-xs font-medium rounded-full border ' +
  'bg-primary-600 text-white border-primary-600 ' +
  'dark:bg-primary-500 dark:border-primary-500';

const INACTIVE_CHIP =
  'px-3 py-1 text-xs font-medium rounded-full border ' +
  'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 ' +
  'dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 ' +
  'dark:hover:bg-gray-700';

export function PriorityHistogram({
  queue,
  initialStatus = 'PENDING',
  bins = 20,
  className,
}: PriorityHistogramProps) {
  const [status, setStatus] = useState<HistogramStatus>(initialStatus);
  const { data, isLoading, isError, error, refetch } = useQueuePriorityHistogram(
    queue.queue_id,
    status,
    bins,
  );

  // AC-1589: Hide the histogram entirely for non-priority queues. A FIFO
  // queue has all items at p=0, so the histogram carries no signal regardless
  // of how many items are present, and the disappear-on-tab-switch behavior
  // (when the selected status has zero items) caused jarring layout shift.
  // For priority queues the container stays mounted so tab switches keep
  // layout stable; the empty-state message below handles zero-item tabs.
  if (queue.priority_field === null) {
    return null;
  }

  const rootClass = [
    'p-4 rounded-lg border bg-white dark:bg-gray-900 ' +
      'border-gray-200 dark:border-gray-700',
    className ?? '',
  ]
    .join(' ')
    .trim();

  return (
    <div data-testid="priority-histogram-root" className={rootClass}>
      <div
        role="toolbar"
        aria-label="Histogram status filter"
        className="flex gap-2 mb-3 flex-wrap"
      >
        {STATUS_OPTIONS.map((s) => {
          const active = s === status;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={active}
              onClick={() => setStatus(s)}
              className={active ? ACTIVE_CHIP : INACTIVE_CHIP}
            >
              {s}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div
          data-testid="histogram-loading"
          role="status"
          aria-live="polite"
          aria-label="Loading priority histogram"
          className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center"
        >
          Loading priority histogram...
        </div>
      )}

      {isError && (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 py-6 text-sm text-red-600 dark:text-red-400"
        >
          <span>
            Error loading histogram: {(error as Error)?.message ?? 'unknown error'}
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-1 text-xs font-medium rounded border border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && data && data.total === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
          No {status} items in this queue
        </div>
      )}

      {!isLoading && !isError && data && data.total > 0 && (
        <>
          <div
            aria-label={`Histogram of priority values, ${data.bin_strategy} mode`}
            className="w-full"
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.bins}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            min={data.min} {'·'} max={data.max} {'·'} n={data.total}
          </div>
        </>
      )}
    </div>
  );
}

export default PriorityHistogram;
